import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core'
import { NoEthereumProviderError } from '@web3-react/injected-connector'
import React from 'react'
import { Colors } from 'src/constants'
import { useInactiveListener, useModal, useWindowSize } from 'src/hooks'
import MetamaskLogo from '../../assets/metamask.png'
import { injected } from '../../connectors'
import { Box, Button, Error, Icon, Row, TitleH2 } from '../index'
import {
  Overlay,
  Text,
  WalletInterfaceContainer,
  WalletSelectButton,
} from './components'

const connectors = [
  {
    name: 'Metamask',
    connector: injected,
    logoSrc: MetamaskLogo,
  },
]

function getErrorMessage(error: Error) {
  if (error instanceof NoEthereumProviderError) {
    return 'No Ethereum browser extension detected, install MetaMask on desktop or visit from a dApp browser on mobile.'
  } else if (error instanceof UnsupportedChainIdError) {
    return "You're connected to an unsupported network."
  } else {
    console.error(error)
    return 'An unknown error occurred. Check the console for more details.'
  }
}

export const ConnectWalletInterface = () => {
  const { toggleModal } = useModal()

  const { connector, activate, deactivate, active, error } = useWeb3React()

  const { isMobile } = useWindowSize()

  // handle logic to recognize the connector currently being activated
  const [activatingConnector, setActivatingConnector] = React.useState<any>()
  React.useEffect(() => {
    if (activatingConnector && activatingConnector === connector) {
      setActivatingConnector(undefined)
    }
  }, [activatingConnector, connector])

  // handle logic to connect in reaction to certain events on the injected ethereum provider, if it exists
  useInactiveListener(!!activatingConnector)

  const isUnsupportedChainIdError = error instanceof UnsupportedChainIdError

  return (
    <Overlay>
      <WalletInterfaceContainer>
        <Box
          roundedCorners
          padding={isMobile ? 'm' : 'xl'}
          backgroundColor={Colors.WOODSMOKE_LIGHT}
        >
          <Box marginBottom="l">
            <TitleH2>Connect to a Wallet</TitleH2>
          </Box>

          {connectors.map((currentConnector) => {
            const activating =
              activatingConnector === currentConnector.connector
            const connected = connector === currentConnector.connector
            const disabled = !!activatingConnector || connected || !!error

            return (
              <Box key={currentConnector.name} marginBottom="l">
                <WalletSelectButton
                  disabled={isUnsupportedChainIdError || disabled || activating}
                  selected={connected && active}
                  onClick={async () => {
                    if (isUnsupportedChainIdError) return
                    setActivatingConnector(currentConnector.connector)
                    await activate(currentConnector.connector)
                    toggleModal('none')
                  }}
                >
                  <Row alignItems="center" justifyContent="space-between">
                    <Text>{currentConnector.name}</Text>
                    <Icon size="m" src={currentConnector.logoSrc} />
                  </Row>
                </WalletSelectButton>
              </Box>
            )
          })}
          {active && (
            <Row justifyContent="center">
              <Button
                buttonType="secondary"
                onClick={() => {
                  deactivate()
                }}
              >
                Deactivate
              </Button>
            </Row>
          )}
        </Box>
        {!!error && (
          <Box marginTop="m">
            <Error errorText={getErrorMessage(error)} />
          </Box>
        )}
      </WalletInterfaceContainer>
    </Overlay>
  )
}
